import {
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import {
  createSuite,
  deleteSuite,
  getSuiteBySlug,
  querySuites,
  updateSuite,
} from '@/features/suites/data';
import {
  gridPaths,
  invalidateGridQuery,
  invalidateMetricsPrefix,
  invalidateSuiteDetail,
  invalidateSuiteRunSummaries,
  invalidateSuites,
  queryKeys,
  useInvalidateQueries,
  useIsAuthenticated,
} from '@/shared/hooks';
import { removeFromResult, upsertIntoResult } from '@/shared/hooks/shared';
import type { ApiError } from '@/shared/data/client';
import type { QueryResult, SuiteDetailDto, SuiteListItemDto } from '@flowqa/shared';

const toSlug = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

export const useSuitesQuery = (
  projectId?: string | null,
  options?: { enabled?: boolean },
): UseQueryResult<QueryResult<SuiteListItemDto>, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  const isEnabled = isAuthenticated && (options?.enabled ?? true);
  return useQuery<QueryResult<SuiteListItemDto>, ApiError>({
    queryKey: queryKeys.suites(projectId, orgKey),
    enabled: isEnabled,
    queryFn: async (): Promise<QueryResult<SuiteListItemDto>> =>
      await querySuites(projectId, orgForQuery, {
        page: 1,
        pageSize: 50,
        sorts: [{ field: 'createdAt', direction: 'desc' }],
        filters: [],
        include: { features: true },
      }),
    staleTime: 30_000,
  });
};

export { useSuiteOptions } from './useSuiteOptions';

export const useSuiteDetailQuery = (
  slug: string | null,
  projectId?: string | null,
): UseQueryResult<SuiteDetailDto | null, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const orgKey = selectedOrganisationId ?? null;
  const effectiveProjectId = projectId ?? selectedProjectId ?? null;
  return useQuery<SuiteDetailDto | null, ApiError>({
    queryKey: queryKeys.suiteDetail(slug, orgKey, effectiveProjectId),
    enabled: isAuthenticated && Boolean(slug),
    queryFn: async (): Promise<SuiteDetailDto | null> =>
      await getSuiteBySlug(slug, effectiveProjectId, selectedOrganisationId ?? undefined),
    staleTime: 15_000,
  });
};

const toSuiteListItem = (suite: SuiteDetailDto): SuiteListItemDto => ({
  id: suite.id,
  slug: suite.slug,
  name: suite.name,
  organisationId: suite.organisationId,
  projectId: suite.projectId,
  project: suite.project,
  features: suite.features,
  latestRun: suite.latestRun,
  latestTestResults: suite.latestTestResults,
  deletedAt: suite.deletedAt,
});

export const useCreateSuite = (): UseMutationResult<
  SuiteDetailDto,
  ApiError,
  { name: string; projectId: string; featureIds?: string[] }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const allKey = queryKeys.suites(undefined, orgKey);
  const projectKey = (projectId: string): ReturnType<typeof queryKeys.suites> =>
    queryKeys.suites(projectId, orgKey);

  return useMutation<
    SuiteDetailDto,
    ApiError,
    { name: string; projectId: string; featureIds?: string[] },
    {
      previousAll?: QueryResult<SuiteListItemDto>;
      previousProject?: QueryResult<SuiteListItemDto>;
      projectId: string;
      tempId: string;
    }
  >({
    mutationFn: async (input) => await createSuite(input, selectedOrganisationId),
    onMutate: (input) => {
      const tempId = `temp-${Date.now()}`;
      const optimistic: SuiteListItemDto = {
        id: tempId,
        slug: toSlug(input.name),
        name: input.name.trim(),
        organisationId: selectedOrganisationId ?? null,
        projectId: input.projectId,
        features: input.featureIds ? { ids: input.featureIds } : undefined,
        deletedAt: null,
      };
      const previousAll = queryClient.getQueryData<QueryResult<SuiteListItemDto>>(allKey);
      const previousProject = queryClient.getQueryData<QueryResult<SuiteListItemDto>>(
        projectKey(input.projectId),
      );
      queryClient.setQueryData<QueryResult<SuiteListItemDto>>(allKey, (prev) =>
        upsertIntoResult(prev, optimistic),
      );
      queryClient.setQueryData<QueryResult<SuiteListItemDto>>(projectKey(input.projectId), (prev) =>
        upsertIntoResult(prev, optimistic),
      );
      return { previousAll, previousProject, projectId: input.projectId, tempId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(allKey, context.previousAll);
      }
      if (context?.previousProject) {
        queryClient.setQueryData(projectKey(context.projectId), context.previousProject);
      }
    },
    onSuccess: async (created, _vars, context) => {
      if (context.tempId) {
        const listItem = toSuiteListItem(created);
        queryClient.setQueryData<QueryResult<SuiteListItemDto>>(allKey, (prev) =>
          upsertIntoResult(removeFromResult(prev, context.tempId), listItem),
        );
        queryClient.setQueryData<QueryResult<SuiteListItemDto>>(
          projectKey(created.projectId),
          (prev) => upsertIntoResult(removeFromResult(prev, context.tempId), listItem),
        );
      }
      await invalidateQueries([
        invalidateSuites(undefined, orgKey),
        invalidateSuiteDetail(null, orgKey, created.projectId),
        invalidateGridQuery(gridPaths.suites),
      ]);
    },
  });
};

export const useUpdateSuite = (): UseMutationResult<
  SuiteDetailDto,
  ApiError,
  { id: string; name?: string; featureIds?: string[]; tags?: string[] },
  {
    previousAll?: QueryResult<SuiteListItemDto>;
    previousProject?: QueryResult<SuiteListItemDto>;
    projectId?: string;
  }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const allKey = queryKeys.suites(undefined, orgKey);
  const projectKey = (projectId: string | undefined): ReturnType<typeof queryKeys.suites> =>
    queryKeys.suites(projectId ?? null, orgKey);

  return useMutation<
    SuiteDetailDto,
    ApiError,
    { id: string; name?: string; featureIds?: string[]; tags?: string[] },
    {
      previousAll?: QueryResult<SuiteListItemDto>;
      previousProject?: QueryResult<SuiteListItemDto>;
      projectId?: string;
    }
  >({
    mutationFn: async (input) => await updateSuite(input, selectedOrganisationId),
    onMutate: (input) => {
      const previousAll = queryClient.getQueryData<QueryResult<SuiteListItemDto>>(allKey);
      const projectId = previousAll?.items.find((item) => item.id === input.id)?.projectId;
      const previousProject = projectId
        ? queryClient.getQueryData<QueryResult<SuiteListItemDto>>(projectKey(projectId))
        : undefined;
      queryClient.setQueryData<QueryResult<SuiteListItemDto>>(allKey, (prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((item) =>
                item.id === input.id
                  ? {
                      ...item,
                      name: input.name?.trim() ?? item.name,
                      features: input.featureIds ? { ids: input.featureIds } : item.features,
                    }
                  : item,
              ),
            }
          : prev,
      );
      if (projectId) {
        queryClient.setQueryData<QueryResult<SuiteListItemDto>>(projectKey(projectId), (prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((item) =>
                  item.id === input.id
                    ? {
                        ...item,
                        name: input.name?.trim() ?? item.name,
                        features: input.featureIds ? { ids: input.featureIds } : item.features,
                      }
                    : item,
                ),
              }
            : prev,
        );
      }
      return { previousAll, previousProject, projectId };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(allKey, context.previousAll);
      }
      if (context?.previousProject && context.projectId) {
        queryClient.setQueryData(projectKey(context.projectId), context.previousProject);
      }
    },
    onSuccess: async (updated) => {
      const listItem = toSuiteListItem(updated);
      queryClient.setQueryData<QueryResult<SuiteListItemDto>>(allKey, (prev) =>
        upsertIntoResult(prev, listItem),
      );
      queryClient.setQueryData<QueryResult<SuiteListItemDto>>(
        projectKey(updated.projectId),
        (prev) => upsertIntoResult(prev, listItem),
      );
      await invalidateQueries([
        invalidateSuites(undefined, orgKey),
        invalidateSuites(updated.projectId, orgKey),
        invalidateSuiteDetail(updated.slug, orgKey, updated.projectId),
        invalidateGridQuery(gridPaths.suites),
        invalidateGridQuery(gridPaths.suiteRuns),
        invalidateGridQuery(gridPaths.testRuns),
        invalidateSuiteRunSummaries(),
        invalidateMetricsPrefix(),
      ]);
    },
  });
};

export const useDeleteSuite = (): UseMutationResult<
  { id: string },
  ApiError,
  { id: string; projectId: string },
  { previousAll?: QueryResult<SuiteListItemDto>; previousProject?: QueryResult<SuiteListItemDto> }
> => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const allKey = queryKeys.suites(undefined, orgKey);
  const projectKey = (projectId: string): ReturnType<typeof queryKeys.suites> =>
    queryKeys.suites(projectId, orgKey);

  return useMutation<
    { id: string },
    ApiError,
    { id: string; projectId: string },
    { previousAll?: QueryResult<SuiteListItemDto>; previousProject?: QueryResult<SuiteListItemDto> }
  >({
    mutationFn: async (input) => await deleteSuite({ id: input.id }, selectedOrganisationId),
    onMutate: (input) => {
      const previousAll = queryClient.getQueryData<QueryResult<SuiteListItemDto>>(allKey);
      const previousProject = queryClient.getQueryData<QueryResult<SuiteListItemDto>>(
        projectKey(input.projectId),
      );
      queryClient.setQueryData<QueryResult<SuiteListItemDto>>(allKey, (prev) =>
        removeFromResult(prev, input.id),
      );
      queryClient.setQueryData<QueryResult<SuiteListItemDto>>(projectKey(input.projectId), (prev) =>
        removeFromResult(prev, input.id),
      );
      return { previousAll, previousProject };
    },
    onError: (_err, input, context) => {
      if (context?.previousAll) {
        queryClient.setQueryData(allKey, context.previousAll);
      }
      if (context?.previousProject) {
        queryClient.setQueryData(projectKey(input.projectId), context.previousProject);
      }
    },
    onSuccess: async (_deleted, input) => {
      await invalidateQueries([
        invalidateSuites(undefined, orgKey),
        invalidateSuites(input.projectId, orgKey),
        invalidateSuiteDetail(null, orgKey, input.projectId),
        invalidateGridQuery(gridPaths.suites),
        invalidateGridQuery(gridPaths.suiteRuns),
        invalidateGridQuery(gridPaths.testRuns),
        invalidateSuiteRunSummaries(),
        invalidateMetricsPrefix(),
      ]);
    },
  });
};

export const useInvalidateSuites = (): ((projectId?: string | null) => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useCallback(
    async (projectId?: string | null): Promise<void> => {
      await invalidateQueries([
        invalidateSuites(projectId, orgKey),
        invalidateGridQuery(gridPaths.suites),
      ]);
    },
    [invalidateQueries, orgKey],
  );
};

export const useInvalidateSuiteDetail = (): ((
  slug: string | null,
  projectId?: string | null,
) => Promise<void>) => {
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const orgKey = selectedOrganisationId ?? null;
  return useCallback(
    async (slug: string | null, projectId?: string | null): Promise<void> => {
      await invalidateQueries([
        invalidateSuiteDetail(slug, orgKey, projectId ?? selectedProjectId ?? null),
      ]);
    },
    [invalidateQueries, orgKey, selectedProjectId],
  );
};
